import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import { EDIT_VIDEO_MODELS, RESOLUTIONS } from '../constants/models';
import * as api from '../services/api';
import Icons from '../components/Icons';
import Lightbox from '../components/Lightbox';
import { groupItemsByDate } from '../utils/grouping';
import toast from 'react-hot-toast';
import { compressImage } from '../utils/imageCompression';
import FuturisticSlider from '../components/FuturisticSlider';
import { downloadFile } from '../utils/downloadFile';
import MediaLibraryModal from '../components/MediaLibraryModal';
import { confirmAlert } from '../utils/confirmAlert';
import AspectRatioSelector from '../components/AspectRatioSelector';

const ASPECT_RATIOS = ['9:16', '3:4', '1:1', '4:3', '16:9'];
const RES_OPTIONS = ['720p', '1080p'];

export default function EditVideoPage({ onShowAuth }) {
  const { user, token } = useAuth();
  const { credits, setCredits, items, refreshItems, removeItem, pendingJobs, setPendingJobs } = useCredits();
  const navigate = useNavigate();

  // Model
  const [vidModel, setVidModel] = useState('wan-2.7');
  const [showModelPicker, setShowModelPicker] = useState(false);

  // Source video
  const [sourceVideo, setSourceVideo] = useState(null);
  const videoInputRef = useRef();

  // Visual references
  const [refImages, setRefImages] = useState([]);

  const [libOpen, setLibOpen] = useState(false);
  const [libTarget, setLibTarget] = useState(null); // 'video' or 'references'

  // Prompt
  const [prompt, setPrompt] = useState('');
  const [negPrompt, setNegPrompt] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const textRef = useRef(null);

  // Settings
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [resolution, setResolution] = useState('1080p');
  const [duration, setDuration] = useState(5);
  const [promptExpansion, setPromptExpansion] = useState(true);
  const [audioSetting, setAudioSetting] = useState('auto');

  // Generation state
  const [loading, setLoading] = useState(false);
  const [genVids, setGenVids] = useState([]);
  const [cd, setCd] = useState(0);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [lbItem, setLbItem] = useState(null);
  const cdRef = useRef();
  const genLockRef = useRef(false);

  const curModel = EDIT_VIDEO_MODELS.find(m => m.id === vidModel) || EDIT_VIDEO_MODELS[0];

  // Cost calculation
  const getCost = () => {
    if (vidModel === 'wan-2.7') {
      const rate = resolution === '1080p' ? 55 : 40;
      return duration * rate;
    }
    const RES_MULT = { '720p': 1, '1080p': 1.5 };
    return Math.ceil(curModel.baseCost * (RES_MULT[resolution] || 1));
  };
  const cost = getCost();

  const recentVideos = items
    .filter(i => i.type === 'video')
    .sort((a, b) => {
      const ta = a.createdAt?.seconds || a.createdAt?._seconds || 0;
      const tb = b.createdAt?.seconds || b.createdAt?._seconds || 0;
      return tb - ta;
    })
    .slice(0, 8);

  const localJobs = pendingJobs.filter(j => j.type === 'edit-video');

  const handleDeleteItem = async (id) => {
    const isConfirmed = await confirmAlert({
      title: 'Delete Creation?',
      message: 'Are you sure you want to delete this creation? This action cannot be undone.'
    });
    if (!isConfirmed) return;
    try {
      await api.deleteGeneration(token, id);
      removeItem(id);
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const startCD = (s) => {
    setCd(s);
    if (cdRef.current) clearInterval(cdRef.current);
    cdRef.current = setInterval(() => {
      setCd(p => { if (p <= 1) { clearInterval(cdRef.current); return 0; } return p - 1; });
    }, 1000);
  };

  useEffect(() => {
    return () => { if (cdRef.current) clearInterval(cdRef.current); };
  }, []);

  useEffect(() => {
    if (vidModel === 'wan-2.7') setDuration(10);
  }, [vidModel]);

  const handleModelChange = (id) => {
    setVidModel(id);
    setShowModelPicker(false);
  };

  // Prompt handling with @ suggestions
  const handlePromptInput = (e) => {
    const val = e.target.value;
    setPrompt(val);

    const target = e.target;
    target.style.height = '0px';
    target.style.height = target.scrollHeight + 'px';

    if (refImages.length > 0 || sourceVideo) {
      const cursorIndex = e.target.selectionStart;
      const textBeforeCursor = val.slice(0, cursorIndex);
      if (textBeforeCursor.match(/@[\w]*$/)) {
        setShowSuggest(true);
      } else {
        setShowSuggest(false);
      }
    } else {
      setShowSuggest(false);
    }
  };

  const insertSuggestion = (type, idx) => {
    const tag = type === 'video' ? '@video ' : `@image${idx + 1} `;
    if (textRef.current) {
      const cursorIndex = textRef.current.selectionStart;
      const textBeforeCursor = prompt.slice(0, cursorIndex);
      const textAfterCursor = prompt.slice(cursorIndex);
      const newTextBefore = textBeforeCursor.replace(/@[\w]*$/, tag);
      const newPrompt = newTextBefore + textAfterCursor;
      setPrompt(newPrompt);
      setShowSuggest(false);
      setTimeout(() => {
        if (textRef.current) {
          textRef.current.focus();
          textRef.current.setSelectionRange(newTextBefore.length, newTextBefore.length);
        }
      }, 0);
    } else {
      setPrompt(prompt.replace(/@[\w]*$/, tag));
      setShowSuggest(false);
    }
  };

  const handleOptimize = async () => {
    if (!prompt.trim()) {
      toast.error('Enter a basic prompt to optimize first.');
      return;
    }
    navigate('/prompts', { state: { prompt } });
  };

  const handleGen = useCallback(async () => {
    if (genLockRef.current) return;
    if (!user) { if (onShowAuth) onShowAuth(); return; }
    if (cd > 0) { toast.error(`Wait ${cd}s`); return; }
    if (!sourceVideo) { toast.error('Upload a source video first'); return; }
    if (!prompt.trim()) { toast.error('Enter a prompt'); return; }
    if (credits < cost) { toast.error(`Need ${cost} credits`); navigate('/subscription'); return; }

    genLockRef.current = true;

    const jobId = `evpend_${Date.now()}`;
    const thumbUrl = sourceVideo ? URL.createObjectURL(sourceVideo) : null;
    const newJob = {
      id: jobId, prompt: prompt.substring(0, 40),
      model: curModel?.label || vidModel, type: 'edit-video', thumb: thumbUrl,
    };
    setPendingJobs(prev => [newJob, ...prev]);
    setLoading(true);
    startCD(20);

    (async () => {
      try {
        const res = await api.editVideo(token, {
          model: vidModel,
          sourceVideo: sourceVideo?.fileUrl || sourceVideo,
          prompt,
          negativePrompt: negPrompt || undefined,
          aspectRatio,
          resolution,
          duration,
          promptExpansion,
          audioSetting,
          referenceImages: refImages.length > 0 ? refImages.map(img => img.fileUrl || img) : undefined,
        });
        setPendingJobs(prev => prev.filter(j => j.id !== jobId));
        if (res.success) {
          const urls = (res.videos || []).map(v => v.url).filter(Boolean);
          if (urls.length > 0) {
            setGenVids(urls);
            toast.success(`Video edited in ${res.generationTime}s! 🎉`);
          } else if (res.videoUrl) {
            setGenVids([res.videoUrl]);
            toast.success('Video edited successfully! 🎉');
          }
          setCredits(res.creditsRemaining);
          refreshItems(true);
        }
      } catch (e) {
        setPendingJobs(prev => prev.filter(j => j.id !== jobId));
        const msg = e.response?.data?.error;
        const st = e.response?.status;
        if (st === 429) { toast.error(msg || 'Rate limited'); startCD(20); }
        else if (st === 402) { toast.error('Not enough credits'); navigate('/subscription'); }
        else if (st === 403) { toast.error(msg || 'Upgrade required'); navigate('/subscription'); }
        else toast.error(msg || 'Video editing failed. Credits refunded.');
      } finally {
        genLockRef.current = false;
        setLoading(false);
      }
    })();
  }, [prompt, vidModel, sourceVideo, aspectRatio, resolution, duration, refImages, cost, credits, cd, token, user, navigate, setCredits, refreshItems, onShowAuth, curModel]);

  return (
    <>
      {/* Model Picker Popup */}
      {showModelPicker && (
        <div className="mp-overlay" onClick={() => setShowModelPicker(false)}>
          <div className="mp-popup" onClick={e => e.stopPropagation()}>
            <div className="mp-header">
              <div className="mp-title">Select Video Edit Model</div>
              <button className="mc-btn" onClick={() => setShowModelPicker(false)}><Icons.X sz={14} /></button>
            </div>
            <div className="mp-body">
              {EDIT_VIDEO_MODELS.map(m => (
                <button key={m.id} className={`mp-item ${vidModel === m.id ? 'on' : ''}`}
                  onClick={() => handleModelChange(m.id)}>
                  {m.icon && Icons[m.icon] ? (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 20, height: 20, marginRight: 10, color: m.color }}>
                      {React.createElement(Icons[m.icon], { sz: 18, color: m.color })}
                    </div>
                  ) : <div className="mp-dot" style={{ background: m.color }} />}
                  <div className="mp-info">
                    <div className="mp-name">
                      {m.label}
                    </div>
                  </div>
                  {vidModel === m.id && (
                    <div className="mp-check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      <button className={`mobile-fab ${showMobilePreview ? 'on' : ''}`} onClick={() => setShowMobilePreview(!showMobilePreview)}>
        <Icons.Scissors sz={22} />
        <span>{showMobilePreview ? 'Close' : 'Preview'}</span>
      </button>

      <section className="panel">
        <div className="phead"><Icons.Vid sz={16} /> Edit Video</div>

        {/* Model Selection */}
        <div className="psec">
          <div className="slbl">Model</div>
          <button className="mp-trigger" onClick={() => setShowModelPicker(true)}>
            <div className="mp-trigger-left">
              {curModel?.icon && Icons[curModel.icon] ? (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 22, height: 22, marginRight: 10, color: curModel.color }}>
                  {React.createElement(Icons[curModel.icon], { sz: 20, color: curModel.color })}
                </div>
              ) : <div className="mp-dot" style={{ background: curModel?.color }} />}
              <div>
                <div className="mp-trigger-name">{curModel?.label}</div>
              </div>
            </div>
            <div className="mp-trigger-right">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
            </div>
          </button>
        </div>

        {/* Source Video Upload */}
        <div className="psec">
          <div className="slbl">Source Video</div>
          <div className={`uzone ${sourceVideo ? 'on' : ''}`} onClick={() => { setLibTarget('video'); setLibOpen(true); }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              const file = e.dataTransfer.files?.[0];
              if (!file) return;
              if (file.size > 100 * 1024 * 1024) { toast.error('Video must be under 100 MB'); return; }
              setSourceVideo(file);
            }}>
            {sourceVideo ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                  <video src={sourceVideo.fileUrl || URL.createObjectURL(sourceVideo)} style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover' }} />
                  <button onClick={(e) => { e.stopPropagation(); setSourceVideo(null); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.9)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--t2)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sourceVideo.name || 'Library Video'}</div>
                </div>
              </div>
            ) : (
              <>
                <div style={{ opacity: .4 }}><Icons.Upload /></div>
                <div style={{ fontSize: 12 }}>Upload source video</div>
                <div style={{ fontSize: 10, color: 'var(--t3)' }}>MP4, MOV, WebM · Max 100MB</div>
              </>
            )}
          </div>
        </div>

        {/* Visual References */}
        <div className="psec" style={{ paddingBottom: 0, borderBottom: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span className="slbl" style={{ margin: 0 }}>Visual References</span>
            <span style={{ fontSize: 10, color: 'var(--t3)' }}>
              Optional · {refImages.length}/3
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {refImages.map((img, i) => (
              <div key={i} style={{
                position: 'relative', width: 44, height: 44, borderRadius: 8,
                overflow: 'hidden', border: '1px solid rgba(139,92,246,.4)',
              }}>
                <img src={img.fileUrl || URL.createObjectURL(img)} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                <div style={{
                  position: 'absolute', bottom: 0, width: '100%', background: 'rgba(0,0,0,.7)',
                  textAlign: 'center', fontSize: 9, fontWeight: 700, color: '#a78bfa'
                }}>@{i + 1}</div>
                <button onClick={() => setRefImages(prev => prev.filter((_, j) => j !== i))} style={{
                  position: 'absolute', top: 1, right: 1, width: 14, height: 14, borderRadius: '50%',
                  background: 'rgba(0,0,0,.7)', border: 'none', color: '#fff', fontSize: 9, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1
                }}>✕</button>
              </div>
            ))}
            {refImages.length < 3 && (
              <button onClick={() => { setLibTarget('references'); setLibOpen(true); }} style={{
                width: 44, height: 44, borderRadius: 8, border: '1px dashed rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.03)', color: 'var(--t2)', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2
              }}>
                <Icons.Plus sz={12} />
              </button>
            )}
          </div>
          <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic' }}>
            Type <span style={{color: '#a78bfa', fontWeight: 'bold'}}>@</span> in your prompt to refer to uploaded images or the source video.
          </div>
        </div>

        {/* Prompt Section */}
        <div className="psec">
          <div className="slbl">Prompt</div>
          <div className="pwrap" style={{ position: 'relative' }}>
            <textarea ref={textRef} className="ptxt"
              placeholder="Describe how you want to edit the video — e.g. 'Add cinematic color grading with warm tones, apply slow-motion to the action scene, change the background to a futuristic city'"
              value={prompt} onChange={handlePromptInput} maxLength={10000} style={{ minHeight: '120px', fontSize: '13px', resize: 'none', overflowY: 'hidden', flex: 'none', display: 'block' }} />
            {showSuggest && (
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, background: '#1e1e2d',
                border: '1px solid rgba(139,92,246,0.5)', borderRadius: 8, padding: 6, zIndex: 100,
                boxShadow: '0 8px 24px rgba(0,0,0,0.6)', display: 'flex', gap: 6, flexWrap: 'wrap',
              }}>
                {sourceVideo && (
                  <div onMouseDown={(e) => { e.preventDefault(); insertSuggestion('video', 0); }} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                    cursor: 'pointer', borderRadius: 6, background: 'rgba(6,182,212,0.15)',
                    border: '1px solid rgba(6,182,212,0.2)', transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(6,182,212,0.3)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(6,182,212,0.15)'}>
                    <Icons.Vid sz={14} />
                    <span style={{fontSize: 12, color: '#06b6d4', fontWeight: 600}}>@video</span>
                  </div>
                )}
                {refImages.map((img, idx) => (
                  <div key={idx} onMouseDown={(e) => { e.preventDefault(); insertSuggestion('image', idx); }} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                    cursor: 'pointer', borderRadius: 6, background: 'rgba(139,92,246,0.15)',
                    border: '1px solid rgba(139,92,246,0.2)', transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.3)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(139,92,246,0.15)'}>
                    <img src={img.fileUrl || URL.createObjectURL(img)} alt="" style={{width: 24, height: 24, borderRadius: 4, objectFit: 'cover'}} />
                    <span style={{fontSize: 12, color: '#a78bfa', fontWeight: 600}}>@image{idx+1}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="pbot">
              <span className="pcnt" style={{ fontSize: '10px' }}>{prompt.length}/10000</span>
              <button
                className="tbtn"
                onClick={handleOptimize}
                disabled={!prompt.trim()}
                style={{
                  background: 'rgba(139,92,246,0.1)',
                  border: '1px solid rgba(139,92,246,0.2)', color: '#fff', fontSize: 10,
                  padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4,
                  opacity: !prompt.trim() ? 0.5 : 1, cursor: 'pointer', zIndex: 5
                }}
              >
                <><Icons.Sparkle sz={10} /> Optimize</>
              </button>
            </div>
          </div>

          {/* Advanced Prompt Section */}
          <div style={{ marginTop: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--t3)', cursor: 'pointer' }} onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? '▾' : '▸'} Advanced prompt settings
            </span>
            {showAdvanced && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 4 }}>Negative prompt</div>
                <textarea className="ptxt" style={{ minHeight: 50, padding: '8px 12px' }}
                  placeholder="blurry, low quality, deformed, watermark, text…"
                  value={negPrompt} onChange={e => setNegPrompt(e.target.value)} />
                <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 6, fontStyle: 'italic' }}>
                  💡 Use negative prompts to exclude unwanted elements from the edit.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Aspect Ratio */}
        <div className="psec">
          <div className="slbl">Aspect Ratio</div>
          <div style={{ marginBottom: 12 }}>
            <AspectRatioSelector
              options={ASPECT_RATIOS}
              value={aspectRatio}
              onChange={setAspectRatio}
            />
          </div>
        </div>

        {/* Resolution */}
        <div className="psec">
          <div className="slbl">Resolution</div>
          <div className="pills" style={{ marginBottom: 12 }}>
            {RES_OPTIONS.map(r => (
              <button key={r} className={`pill ${resolution === r ? 'on' : ''}`} onClick={() => setResolution(r)}>
                {r} {r === '1080p' && '(+50%)'}
              </button>
            ))}
          </div>
        </div>

        {/* Duration Slider */}
        <div className="psec">
          <FuturisticSlider
            label="Duration"
            min={vidModel === 'wan-2.7' ? 3 : 2}
            max={10}
            step={1}
            value={duration}
            onChange={val => setDuration(val)}
            onReset={() => setDuration(5)}
          />
        </div>

        {/* Prompt Expansion Toggle */}
        <div className="psec">
          <div className="trow">
            <span className="tlbl"><Icons.Sparkle sz={13} /> Prompt Expansion</span>
            <div className={`tog ${promptExpansion ? 'on' : ''}`} onClick={() => setPromptExpansion(!promptExpansion)}><div className="togk" /></div>
          </div>
          <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 8 }}>
            {promptExpansion ? 'AI will expand your prompt for richer detail' : 'Use prompt exactly as written'}
          </div>

          {/* Audio Setting */}
          <div className="slbl" style={{ marginTop: 8 }}>Audio</div>
          <div className="pills" style={{ marginBottom: 4 }}>
            <button className={`pill ${audioSetting === 'auto' ? 'on' : ''}`} onClick={() => setAudioSetting('auto')}>🎵 Auto</button>
            <button className={`pill ${audioSetting === 'origin' ? 'on' : ''}`} onClick={() => setAudioSetting('origin')}>🔊 Keep Original</button>
          </div>
          <div style={{ fontSize: 10, color: 'var(--t3)' }}>
            {audioSetting === 'auto' ? 'AI decides whether to regenerate audio based on the prompt' : 'Original video audio will be preserved'}
          </div>
        </div>

        {/* Generate Button */}
        <div className="gen-sticky">
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap', textAlign: 'center' }}>
            <strong style={{ color: curModel.color }}>{curModel.label}</strong>
            <span>· {duration}s</span>
            <span>· {aspectRatio}</span>
            <span>· {resolution}</span>
          </div>
          <div className="cost-row">
            <span className="cost-l">Total Cost</span>
            <span className="cost-v">{cost} cr → {Math.max(0, (credits || 0) - cost)} left</span>
          </div>
          <button className={`gbtn ${loading ? 'ld' : ''}`} onClick={handleGen}
            disabled={loading || (user && credits < cost) || cd > 0}>
            {cd > 0 ? `Wait ${cd}s` :
              !user ? <>✨ Sign up to Edit Video</> :
              <><Icons.Wand sz={15} /> Edit Video</>}
          </button>
          {user && credits < cost && (
            <p style={{ textAlign: 'center', color: 'var(--rd)', fontSize: 11, marginTop: 6, cursor: 'pointer' }}
              onClick={() => navigate('/subscription')}>Insufficient credits — <u>upgrade</u></p>
          )}
          {cd > 0 && <p style={{ textAlign: 'center', fontSize: 10, color: 'var(--or)', marginTop: 6 }}>Cooldown: {cd}s</p>}
        </div>
      </section>

      {/* Main Canvas */}
      <div className={`main ${showMobilePreview ? 'mobile-preview-open' : ''}`}>
        <div className="mtbar">
          <button className="mtab main-back-mobile" onClick={() => setShowMobilePreview(false)}>
            <Icons.X sz={13} /> Close
          </button>
          <div className="mtab on">Preview</div>
          <div style={{ flex: 1 }} />
          <div className="mtab" onClick={() => navigate('/gallery')} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icons.Grid sz={13} /> Creations
          </div>
        </div>
        <div className="mbody">
          {/* Preview area */}
          {genVids.length > 0 && (
            <div className="prev">
              <div className="prev-wrap">
                <div className="prev-toolbar">
                  <div className="prev-tb-right" style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <button className="prev-icon-btn" onClick={() => downloadFile(genVids[0], token, true).catch(() => toast.error('Download failed'))} title="Download">
                      <Icons.DL sz={15} />
                    </button>
                    <button className="prev-icon-btn del" onClick={async () => {
                      const isConfirmed = await confirmAlert({
                        title: 'Clear Results?',
                        message: 'Are you sure you want to clear these results?'
                      });
                      if (isConfirmed) setGenVids([]);
                    }} title="Clear">
                      <Icons.Trash sz={15} />
                    </button>
                  </div>
                </div>
                {genVids.length === 1 ? (
                  <video src={genVids[0]} controls autoPlay loop style={{ width: '100%', borderRadius: 'var(--r3)', background: '#000' }} />
                ) : (
                  <div className="ggrid" style={{ marginBottom: 12 }}>
                    {genVids.map((url, i) => (
                      <div key={i} className="gitem" onClick={() => setLbItem({ videoUrl: url, prompt, modelName: curModel?.label })}>
                        <video src={url} muted loop playsInline
                          onMouseEnter={e => e.target.play()}
                          onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div className="gbadge" style={{ background: 'rgba(0,0,0,.6)' }}>{i + 1}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {(localJobs.length > 0 || recentVideos.length > 0) && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--t2)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icons.Vid sz={13} /> {localJobs.length > 0 ? 'Processing...' : 'Recent Videos'}
              </div>
              <div className="ggrid">
                {/* Pending shimmer cards */}
                {localJobs.map(j => (
                  <div key={j.id} className="gitem-pending" style={{ position: 'relative' }}>
                    {j.thumb && (
                      <video src={j.thumb} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--r2)', opacity: 0.45 }} />
                    )}
                    <div className="pend-label" style={{ position: 'relative', zIndex: 1 }}>{j.prompt || 'Editing...'}</div>
                    <div className="pend-model" style={{ position: 'relative', zIndex: 1 }}>{j.model}</div>
                    <div className="pend-bar-wrap" style={{ position: 'relative', zIndex: 1 }}><div className="pend-bar" /></div>
                  </div>
                ))}
              </div>

              <div className="grouped-collections">
                {groupItemsByDate(recentVideos).map(group => (
                  <div key={group.label} className="date-group-wrap">
                    <div className="date-group-label" style={{ marginBottom: 12 }}>{group.label}</div>
                    <div className="ggrid">
                      {group.items.map(i => (
                        <div key={i.id} className="gitem" onClick={() => setLbItem(i)}>
                          {i.videoUrl ? (
                            <video src={i.videoUrl} muted loop playsInline onMouseEnter={e => e.target.play()} onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <img src={i.imageUrl} alt="" />
                          )}
                          <div className="gbadge" style={{ background: 'rgba(6,182,212,.12)', color: '#06b6d4' }}>🎬</div>
                          <div className="gact" style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6, zIndex: 15, opacity: 0, transition: 'opacity 0.2s' }}>
                            <button onClick={(e) => { e.stopPropagation(); import('../utils/downloadMedia').then(m => m.downloadMedia(i.videoUrl || i.imageUrl, i.type, token)); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#fff', display: 'flex' }}><Icons.DL sz={14} /></button>
                            <button onClick={(e) => { e.stopPropagation(); if(typeof handleDeleteItem === 'function') handleDeleteItem(i.id || i.$id); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#ef4444', display: 'flex' }}><Icons.Trash sz={14} /></button>
                          </div>
                          <div className="gov">
                            <div className="gm">
                              <div style={{ fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{i.prompt}</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{i.modelName || i.model}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {genVids.length === 0 && localJobs.length === 0 && recentVideos.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--t3)', fontSize: 13, flexDirection: 'column', gap: 12 }}>
              <Icons.Vid sz={32} />
              <p>Upload a video and describe your edits</p>
            </div>
          )}
        </div>
      </div>

      <Lightbox item={lbItem} onClose={() => setLbItem(null)} onDelete={handleDeleteItem} />

      <MediaLibraryModal
        open={libOpen}
        onClose={() => setLibOpen(false)}
        allowedTypes={libTarget === 'video' ? ['video'] : ['image']}
        maxSelect={libTarget === 'video' ? 1 : 3}
        maxTypeCounts={libTarget === 'video' ? { video: 1, image: 0, audio: 0 } : { image: 3, video: 0, audio: 0 }}
        selectedAssets={libTarget === 'references' ? refImages : (sourceVideo ? [sourceVideo] : [])}
        onSelect={(assets) => {
          if (libTarget === 'video') {
            setSourceVideo(assets[0]);
          } else {
            setRefImages(assets);
          }
        }}
      />
    </>
  );
}


